import { pickSources, sourceCollections } from "../data/sourceRegistry";
import { companyContextRecords, peopleProfiles } from "../data/peopleProfiles";

const pageNotice =
  "This page summarizes public records, third-party reporting, and disputed complaint materials. It does not state that unresolved allegations are true or that any person or entity violated the law unless a cited regulator or court record says so.";

const commonResources = pickSources([
  "cfpbComplaintPortal",
  "nmlsConsumerAccess",
  "regulationZ",
]);

const mutualRecordSources = [
  "goodJobsFirstParent",
  "goodJobsFirstMassachusetts2025",
  "massachusettsConsentOrder2025",
  "goodJobsFirstSouthCarolina2025",
  "southCarolinaDoiPdf2025",
  "goodJobsFirstSouthDakota2024",
  "southDakotaConsentOrder2024",
  "goodJobsFirstConnecticut2008",
];

const builderSources = [
  "homesSorenUnit303",
  "vickiSmithLinkedIn",
  "pulteGroupLinkedIn",
  "fox5PulteDelWebb2026",
];

const mutualMortgageContext = companyContextRecords.mutualOfOmahaMortgage;
const pulteGroupContext = companyContextRecords.pulteGroup;
const vickiSmith = peopleProfiles.vickiSmith;
const tonyFragale = mutualMortgageContext.leadership[0];

export const mutualMortgagePages = [
  {
    slug: "/source-ma-mutual-omaha-mortgage-2025",
    title: "Source Record: Massachusetts 2025 Mutual of Omaha Mortgage",
    linkTitle: "MA 2025 Mortgage Record",
    metaTitle: "Massachusetts 2025 Mutual of Omaha Mortgage Record | CFPB Mortgage Complaint",
    description:
      "Source page for the 2025 Massachusetts Division of Banks Mutual of Omaha Mortgage consent order and Good Jobs First record.",
    subheading:
      "A sourced regulatory record involving advertising and paid-content separation, not a finding on the Dupont loans.",
    notice: pageNotice,
    companyContext: mutualMortgageContext,
    facts: [
      { label: "Company", value: "Mutual of Omaha Mortgage, Inc." },
      { label: "Agency", value: "Massachusetts Division of Banks" },
      { label: "Date", value: "April 14, 2025" },
      { label: "Penalty listed", value: "$65,000" },
      { label: "Primary offense listed", value: "Consumer protection violation" },
    ],
    sections: [
      {
        heading: "What the record establishes",
        paragraphs: [
          "Good Jobs First lists a 2025 Massachusetts state agency action against Mutual of Omaha Mortgage, Inc. with a $65,000 penalty.",
          "The Massachusetts consent order states that the matter was resolved by agreement and that Mutual of Omaha Mortgage did not admit allegations or the existence of a violation for purposes of settlement.",
        ],
      },
      {
        heading: "Subject of the order",
        paragraphs: [
          "The order addresses advertising and paid-content separation on a website described in the order as operated through a wholly owned subsidiary.",
          "The order requires clearer visual separation of advertisements or paid content from ratings, reviews, articles, or educational content, along with policy and procedure updates.",
        ],
      },
      {
        heading: "How this site uses it",
        paragraphs: [
          "This source is used as public regulatory context for Mutual of Omaha Mortgage. It is not presented as proof of the CFPB complaint allegations involving 700 N Dupont Ave.",
          "The record is relevant because it shows a recent mortgage-related consumer protection consent order involving the same mortgage company, while the specific conduct differs from the loan-origination allegations in this dossier.",
        ],
      },
    ],
    sources: pickSources(["goodJobsFirstMassachusetts2025", "massachusettsConsentOrder2025"]),
    resources: commonResources,
  },
  {
    slug: "/source-sc-mutual-omaha-insurance-2025",
    title: "Source Record: South Carolina 2025 Mutual of Omaha Insurance",
    linkTitle: "SC 2025 Insurance Record",
    metaTitle: "South Carolina 2025 Mutual of Omaha Insurance Record | CFPB Mortgage Complaint",
    description:
      "Source page for the 2025 South Carolina Department of Insurance Mutual of Omaha Insurance Company record.",
    subheading:
      "A public insurance enforcement record connected to the Mutual of Omaha parent family.",
    notice: pageNotice,
    facts: [
      { label: "Company", value: "Mutual of Omaha Insurance Company" },
      { label: "Agency", value: "South Carolina Department of Insurance" },
      { label: "Date", value: "May 28, 2025" },
      { label: "Penalty listed", value: "$25,000" },
      { label: "Primary offense listed", value: "Insurance violation" },
    ],
    sections: [
      {
        heading: "What the record establishes",
        paragraphs: [
          "Good Jobs First lists a 2025 South Carolina state agency action against Mutual of Omaha Insurance Company with a $25,000 penalty.",
          "The record identifies Mutual of Omaha as the current parent company and lists the action as a civil agency action.",
        ],
      },
      {
        heading: "Use and limits",
        paragraphs: [
          "This source is included as external public-record context for the broader Mutual of Omaha corporate family.",
          "It does not establish any fact about the Dupont mortgage files, Rob Shell, Legacy South, or the alleged product steering in the CFPB complaint.",
        ],
      },
    ],
    sources: pickSources(["goodJobsFirstSouthCarolina2025", "southCarolinaDoiPdf2025"]),
    resources: commonResources,
  },
  {
    slug: "/source-sd-united-omaha-life-2024",
    title: "Source Record: South Dakota 2024 United of Omaha Life",
    linkTitle: "SD 2024 United of Omaha Record",
    metaTitle: "South Dakota 2024 United of Omaha Life Record | CFPB Mortgage Complaint",
    description:
      "Source page for the 2024 South Dakota Division of Insurance United of Omaha Life Insurance Company consent order.",
    subheading:
      "A public insurance consent order involving a Mutual of Omaha related company.",
    notice: pageNotice,
    facts: [
      { label: "Company", value: "United of Omaha Life Insurance Company" },
      { label: "Agency", value: "South Dakota Division of Insurance" },
      { label: "Year", value: "2024" },
      { label: "Penalty listed", value: "$20,000" },
      { label: "Issue described", value: "Minimum loss ratio for accident-only policies" },
    ],
    sections: [
      {
        heading: "What the consent order says",
        paragraphs: [
          "The South Dakota order states that the Division investigated United of Omaha Life Insurance Company's insurance-related activities in South Dakota.",
          "The Division alleged that conditionally renewable accident-only policies did not meet a 60 percent minimum loss ratio requirement and that the company agreed to a $20,000 monetary penalty in lieu of contesting the matter formally.",
        ],
      },
      {
        heading: "How this site uses it",
        paragraphs: [
          "This record is included to document a specific public agency action in the Mutual of Omaha related-company record.",
          "It is not mortgage conduct evidence and should not be used as a substitute for loan-file records, disclosure logs, rate sheets, or compensation documents in the Dupont matter.",
        ],
      },
    ],
    sources: pickSources(["goodJobsFirstSouthDakota2024", "southDakotaConsentOrder2024"]),
    resources: commonResources,
  },
  {
    slug: "/source-ct-mutual-omaha-kickbacks-2008",
    title: "Source Record: Connecticut 2008 Kickbacks and Bribery Record",
    linkTitle: "CT 2008 Kickbacks Record",
    metaTitle: "Connecticut 2008 Mutual of Omaha Kickbacks Record | CFPB Mortgage Complaint",
    description:
      "Source page for the Good Jobs First Connecticut 2008 Mutual of Omaha Insurance Co. kickbacks and bribery record.",
    subheading:
      "A separate public record involving insurance-related compensation allegations, not a finding on the Dupont loans.",
    notice: pageNotice,
    facts: [
      { label: "Company", value: "Mutual of Omaha Insurance Co." },
      { label: "Agency listed", value: "Connecticut Attorney General" },
      { label: "Year", value: "2008" },
      { label: "Penalty listed", value: "$1,700,000" },
      { label: "Primary offense listed", value: "Kickbacks and bribery" },
    ],
    sections: [
      {
        heading: "What the record establishes",
        paragraphs: [
          "Good Jobs First lists a 2008 Connecticut Attorney General record for Mutual of Omaha Insurance Co. under the primary offense type 'kickbacks and bribery' with a $1.7 million penalty.",
          "The parent summary lists this as the single competition-related offense in the Mutual of Omaha parent-company summary.",
        ],
      },
      {
        heading: "Why it is included",
        paragraphs: [
          "The complaint in this dossier raises RESPA and anti-kickback questions in a separate mortgage context. This Connecticut record is therefore useful background on why compensation transparency matters.",
          "The Connecticut record does not prove that a builder-lender kickback occurred at Legacy South, Mutual of Omaha Mortgage, or in the Dupont transactions.",
        ],
      },
      {
        heading: "Use in the investigative question",
        paragraphs: [
          "This page should be cited only for what the Good Jobs First record lists: company, year, agency, penalty, and offense category.",
          "Any mortgage-specific bribery or kickback question must still be tested through loan-level records, referral agreements, builder incentive documents, and compensation files.",
        ],
      },
    ],
    sources: pickSources(["goodJobsFirstConnecticut2008", "goodJobsFirstParent"]),
    resources: commonResources,
  },
  {
    slug: "/mutual-mortgage-profile",
    title: "Mutual of Omaha Mortgage Profile",
    linkTitle: "Mutual Mortgage Profile",
    metaTitle: "Mutual of Omaha Mortgage Profile | CFPB Mortgage Complaint",
    description:
      "Public profile of Mutual of Omaha Mortgage, licensing references, and the role the company plays in the complaint record.",
    subheading: "Company context for the mortgage lender and broker named in the dispute.",
    notice: pageNotice,
    companyContext: mutualMortgageContext,
    facts: [
      { label: "Company website", value: "mutualmortgage.com" },
      { label: "Company NMLS listed publicly", value: "1025894" },
      { label: "Public role in dispute", value: "Mortgage broker and lender channel named in complaint materials" },
    ],
    sections: [
      {
        heading: "Public company context",
        paragraphs: [
          "Mutual of Omaha Mortgage presents itself publicly as a national mortgage company offering purchase, refinance, VA, investment-property, construction, home equity, and related mortgage products.",
          "The company website footer lists Mutual of Omaha Mortgage, Inc. as NMLS ID 1025894 and includes state licensing references.",
        ],
      },
      {
        heading: "Public leadership context",
        paragraphs: [
          `Mutual of Omaha Mortgage's public profile for ${tonyFragale.displayName} lists the role "${tonyFragale.role}" and ${tonyFragale.license.displayValue}.`,
          tonyFragale.dossierConnection.statement,
        ],
        sources: pickSources(tonyFragale.sourceKeys),
      },
      {
        heading: "Role in the complaint record",
        paragraphs: [
          "The complaint names Mutual of Omaha Mortgage in connection with the origination path for two investment-property loans at 700 N Dupont Ave.",
          "Mutual states the DSCR product was suitable based on the borrower's qualification profile. The rebuttal argues that lower-cost or conventional options were not meaningfully presented in the active decision window.",
        ],
      },
      {
        heading: "What should be reviewed",
        list: [
          "The product menu available on the lock dates for both loans.",
          "Any internal analysis of conventional eligibility or decline rationale.",
          "The compensation structure for brokered DSCR products compared with in-house options.",
          "Disclosure delivery logs and borrower acknowledgment timestamps.",
        ],
      },
    ],
    sources: pickSources(["mutualMortgageHome", "mutualMortgageBrentwood"]),
    resources: commonResources,
  },
  {
    slug: "/rob-shell-profile",
    title: "Rob Shell Public Profile",
    linkTitle: "Rob Shell Profile",
    metaTitle: "Rob Shell Public Profile | CFPB Mortgage Complaint",
    description:
      "Public profile information for Rob Shell, the Mutual of Omaha Mortgage loan originator identified in the complaint record.",
    subheading:
      "Public role, NMLS identifier, and Brentwood office context without unnecessary personal details.",
    notice: pageNotice,
    facts: [
      { label: "Name used publicly", value: "Rob Shell / Robert Hillman Shell" },
      { label: "NMLS listed", value: "2226086" },
      { label: "LinkedIn profile", value: "linkedin.com/in/rob-shell-93622325/" },
      { label: "Public title", value: "Mortgage Loan Originator" },
      { label: "Office listed", value: "Brentwood, Tennessee" },
    ],
    sections: [
      {
        heading: "Public profile facts",
        paragraphs: [
          "Mutual of Omaha Mortgage's public loan officer page identifies Rob Shell as a mortgage loan originator with NMLS #2226086.",
          "The public LinkedIn profile linked for this dossier uses the URL linkedin.com/in/rob-shell-93622325/ and is labeled in the source list as Robert Hillman Shell.",
          "The same Mutual profile lists a Brentwood, Tennessee office address. This dossier does not repeat personal contact details unless they are needed for a regulator-facing record.",
        ],
      },
      {
        heading: "Connection to this dossier",
        paragraphs: [
          "The complaint identifies Robert Hillman Shell as the mortgage professional involved in the two disputed Dupont transactions.",
          "The factual dispute centers on what loan options were presented, what was documented, and whether the borrower was given a meaningful opportunity to compare alternatives.",
        ],
      },
      {
        heading: "Vicki Smith profile information",
        paragraphs: [
          "Vicki Smith is also identified in this dossier in the builder-side sales context for the Soren development.",
          "Public profile materials reviewed for this site identify Vicki Smith as a New Home Sales Consultant with PulteGroup in the Nashville Metropolitan Area, while complaint materials identify her in the Legacy South sales context.",
          "Homes.com / Realtracs listing context separately identifies Vicki Smith with Pulte Homes Tennessee on a 2025 Soren listing. This page includes that information as public professional context, not as a finding that unresolved allegations are true.",
        ],
        list: [
          "Public title listed: New Home Sales Consultant.",
          "Employer listed publicly: PulteGroup.",
          "Public area listed: Nashville Metropolitan Area.",
          "Complaint role: Legacy South sales counselor / affiliate broker in complaint materials.",
          "License number: not established by current cited sources.",
          "LinkedIn profile referenced: linkedin.com/in/vicki-smith-davidweekleyhomes/.",
        ],
        links: [
          { label: "Vicki Smith public profile and timeline", to: "/vicki-smith-timeline" },
          { label: "Vicki Smith PulteGroup New Home Sales Consultant", to: "/vicki-smith-pultegroup-new-home-sales-consultant" },
          { label: "Vicki Smith Legacy South Soren timeline", to: "/vicki-smith-legacy-south-soren-timeline" },
          { label: "Vicki Smith LinkedIn public record", to: "/vicki-smith-linkedin-public-record" },
        ],
        sources: pickSources(["vickiSmithLinkedIn", "homesSorenUnit303", "pulteGroupLinkedIn"]),
      },
      {
        heading: "Records needed for a fair review",
        list: [
          "Loan officer notes and communications for both files.",
          "Any comparison materials sent before the borrower committed to the DSCR path.",
          "Loan-level compensation records.",
          "Rate sheets and product matrices available on the relevant dates.",
        ],
      },
      {
        heading: "Related Rob Shell pages",
        paragraphs: [
          "These index pages separate public profile facts, NMLS identity, LinkedIn context, and DSCR steering allegations for search and source clarity.",
        ],
        links: [
          { label: "Rob Shell and Mutual of Omaha Mortgage", to: "/rob-shell-mutual-of-omaha-mortgage" },
          { label: "Robert Hillman Shell NMLS 2226086", to: "/robert-hillman-shell-nmls-2226086" },
          { label: "Rob Shell DSCR loan steering allegations", to: "/rob-shell-dscr-loan-steering-allegations" },
          { label: "Rob Shell LinkedIn public record", to: "/rob-shell-linkedin-public-record" },
        ],
      },
    ],
    sources: pickSources([
      "robShellOfficial",
      "robShellLinkedIn",
      "nmlsConsumerAccess",
      "vickiSmithLinkedIn",
      "homesSorenUnit303",
      "pulteGroupLinkedIn",
    ]),
    resources: commonResources,
  },
  {
    slug: "/rob-shell-complaint-role",
    title: "Rob Shell Role in the Complaint Record",
    linkTitle: "Rob Shell Complaint Role",
    metaTitle: "Rob Shell Role in the Complaint Record | CFPB Mortgage Complaint",
    description:
      "Complaint and rebuttal summary of Rob Shell's alleged role in product presentation, disclosures, and DSCR loan selection.",
    subheading:
      "The complaint allegations, Mutual's response, and the records needed to test both sides.",
    notice: pageNotice,
    sections: [
      {
        heading: "Complaint allegations",
        paragraphs: [
          "The complaint alleges that Rob Shell represented the non-QM DSCR interest-only product as the only practical loan option for the Dupont properties.",
          "The complaint further alleges that requested rate sheets, written conventional comparisons, and lower-cost alternatives were not provided before the borrower proceeded.",
        ],
      },
      {
        heading: "Mutual's stated response",
        paragraphs: [
          "Mutual states the borrower did not meet conventional personal-income qualification expectations at the time and that the DSCR program was explained and accepted.",
          "Mutual also states that disclosures were provided and that its internal review did not identify coercion, steering, inflated charges, or improper compensation.",
        ],
      },
      {
        heading: "Open factual questions",
        list: [
          "What products were available to this borrower on the lock dates?",
          "Were conventional options evaluated in writing before the DSCR path was selected?",
          "What communications show whether the borrower requested alternatives?",
          "What compensation applied to the chosen channel and any alternatives?",
        ],
      },
      {
        heading: "Related Rob Shell pages",
        links: [
          { label: "Rob Shell and Mutual of Omaha Mortgage", to: "/rob-shell-mutual-of-omaha-mortgage" },
          { label: "Robert Hillman Shell NMLS 2226086", to: "/robert-hillman-shell-nmls-2226086" },
          { label: "Rob Shell DSCR loan steering allegations", to: "/rob-shell-dscr-loan-steering-allegations" },
          { label: "Rob Shell LinkedIn public record", to: "/rob-shell-linkedin-public-record" },
        ],
      },
    ],
    sources: pickSources(["robShellOfficial", "robShellLinkedIn", "regulationZ"]),
    resources: commonResources,
  },
  {
    slug: "/brentwood-office-builder-channel",
    title: "Brentwood Office and Builder Channel Context",
    linkTitle: "Brentwood Office Channel",
    metaTitle: "Brentwood Office and Builder Channel Context | CFPB Mortgage Complaint",
    description:
      "Context on the Mutual of Omaha Mortgage Brentwood office and why builder-channel documentation matters in the complaint review.",
    subheading:
      "A public location link and the records needed to evaluate builder-lender coordination.",
    notice: pageNotice,
    companyContext: mutualMortgageContext,
    facts: [
      { label: "Office", value: "Mutual of Omaha Mortgage, Brentwood, Tennessee" },
      { label: "Public address", value: "9005 Overlook Blvd, Suite 118, Brentwood, TN 37027" },
    ],
    sections: [
      {
        heading: "Why the office matters",
        paragraphs: [
          "The Brentwood office appears in public Mutual of Omaha Mortgage and Rob Shell profile materials.",
          "The office context matters because the complaint concerns loan origination tied to a new-home development and alleged pressure around closing timing.",
        ],
      },
      {
        heading: "Builder-channel questions",
        paragraphs: [
          "The complaint alleges that builder-side pressure and lender-side product presentation worked together to limit meaningful choice.",
          "Those allegations require records showing whether any builder, sales counselor, or lender relationship influenced the loan path or closing timeline.",
        ],
      },
      {
        heading: "Documents that would clarify the relationship",
        list: [
          "Referral agreements or approved lender arrangements.",
          "Builder incentive and preferred-title documents.",
          "Communications among the sales counselor, lender, title company, and borrower.",
          "Any policy governing builder-lender communications for the development.",
        ],
      },
    ],
    sources: pickSources(["mutualMortgageBrentwood", "robShellOfficial"]),
    resources: commonResources,
  },
  {
    slug: "/mutual-mortgage-licensing-and-programs",
    title: "Mutual Mortgage Licensing and Program Context",
    linkTitle: "Licensing and Programs",
    metaTitle: "Mutual Mortgage Licensing and Program Context | CFPB Mortgage Complaint",
    description:
      "Public licensing and loan-program context for Mutual of Omaha Mortgage as it relates to the complaint review.",
    subheading:
      "How public licensing and advertised programs frame the records requested in this dispute.",
    notice: pageNotice,
    companyContext: mutualMortgageContext,
    facts: [
      { label: "Public NMLS", value: "Mutual of Omaha Mortgage, Inc. NMLS ID 1025894" },
      { label: "Relevant public category", value: "Investment property loan information appears on the company site" },
    ],
    sections: [
      {
        heading: "Public licensing reference",
        paragraphs: [
          "Mutual of Omaha Mortgage's website lists the company NMLS ID and state licensing information in its legal footer.",
          "Licensing context does not decide the complaint, but it helps readers identify the entity and public regulatory context.",
        ],
      },
      {
        heading: "Program context",
        paragraphs: [
          "The company site publicly presents multiple loan categories, including investment-property resources.",
          "The complaint does not turn on whether Mutual offers multiple programs generally. It turns on what was available, offered, and documented for the specific borrower and dates at issue.",
        ],
      },
      {
        heading: "Record focus",
        list: [
          "Product matrices for October 18, 2023 and December 15, 2023 application periods.",
          "Lock-date rate sheets for both subject files.",
          "Program eligibility notes for the borrower and properties.",
          "Any written explanation for excluding or not presenting alternatives.",
        ],
      },
    ],
    sources: pickSources(["mutualMortgageHome", "nmlsConsumerAccess"]),
    resources: commonResources,
  },
  {
    slug: "/company-advertising-context",
    title: "Company Advertising Context",
    linkTitle: "Advertising Context",
    metaTitle: "Company Advertising Context | CFPB Mortgage Complaint",
    description:
      "How the Massachusetts advertising consent order is used as public context without overstating its relevance to the Dupont loan dispute.",
    subheading:
      "A careful bridge between a public regulatory record and the complaint's transparency concerns.",
    notice: pageNotice,
    companyContext: mutualMortgageContext,
    sections: [
      {
        heading: "What the 2025 order involved",
        paragraphs: [
          "The Massachusetts consent order involved allegations about advertising and paid content on a website, including whether advertisements were clearly separated from recommendations or educational content.",
          "Mutual resolved the matter without admitting allegations or the existence of a violation.",
        ],
      },
      {
        heading: "Why it belongs in the dossier",
        paragraphs: [
          "The complaint raises transparency concerns in a different setting: loan-option presentation, disclosure timing, and product suitability.",
          "The advertising order is therefore used as public background on transparency and consumer-protection oversight, not as proof that the Dupont loan allegations are true.",
        ],
      },
      {
        heading: "How to read the record",
        paragraphs: [
          "Pages should say that the order exists, summarize its subject, and state its limits.",
          "Pages should not imply that an advertising consent order proves mortgage steering, bribery, or kickbacks in the Dupont transactions.",
        ],
      },
    ],
    sources: pickSources(["massachusettsConsentOrder2025", "goodJobsFirstMassachusetts2025"]),
    resources: commonResources,
  },
  {
    slug: "/mutual-mortgage-public-complaints",
    title: "Other Public Mutual Mortgage Complaints",
    linkTitle: "Public Complaints",
    metaTitle: "Other Mutual of Omaha Mortgage Public Complaints | CFPB Mortgage Complaint",
    description:
      "Source-linked public complaints and reviews involving Mutual of Omaha Mortgage, closing pressure, rate or fee changes, appraisal fees, and product-transparency concerns.",
    subheading:
      "A source page for other buyer and borrower reports that resemble the complaint's closing-pressure and transparency concerns.",
    notice: pageNotice,
    companyContext: mutualMortgageContext,
    facts: [
      { label: "Source type", value: "BBB complaints, consumer reviews, and mortgage-review coverage" },
      { label: "Framing rule", value: "Consumer reports are allegations or experiences, not court findings" },
      { label: "Review focus", value: "Closing pressure, rate or fee changes, product switches, and appraisal-fee disputes" },
    ],
    sections: [
      {
        heading: "Why this page exists",
        paragraphs: [
          "The Dupont complaint alleges that Mutual of Omaha Mortgage and related sales-channel participants used timing pressure and incomplete option presentation to push the buyer toward closing.",
          "This page links other public sources where borrowers describe similar themes: unexpected rate or fee changes, alleged bait-and-switch conduct, delayed or missing documents, product changes that were not clearly understood, appraisal-fee disputes, and pressure near settlement.",
          "The page does not state that Mutual of Omaha Mortgage committed mortgage fraud in any specific outside matter unless a cited regulator or court record makes that finding.",
        ],
      },
      {
        heading: "BBB complaint patterns",
        paragraphs: [
          "BBB lists complaints for Mutual of Omaha Mortgage's headquarters and corporate-owned locations and reports a multi-year complaint total for the profile.",
          "One July 2025 BBB complaint states that a borrower closed on a home purchase on May 30, 2025, alleged the company failed to reimburse agreed loan costs, alleged the final rate was not close to the original offer, and described the experience as a bait-and-switch. Mutual disputed bait-and-switch tactics, cited rate-lock and market-condition explanations, and later agreed without admitting fault to reimburse a lender credit.",
          "Other BBB complaints on the same profile involve appraisal-fee refunds, hard-credit-pull disputes, loan-process frustration, advertising/mailers, and rate or document issues. Those records are useful for pattern review, but each complaint must be read with the company's response and status.",
        ],
        sources: pickSources(["bbbMutualMortgageComplaints", "bbbMutualMortgageComplaintsPageThree"]),
      },
      {
        heading: "Consumer review reports",
        paragraphs: [
          "Trustpilot includes negative borrower reviews alleging unclear communication, late-stage document issues, product or loan-amount changes, and frustration with sold or transferred servicing.",
          "One review summarized on Trustpilot says another lender told the reviewer Mutual of Omaha Mortgage had changed the transaction from conventional to FHA and that the reviewer did not understand the implications at the time. Another alleges revised documents were delayed, documents were inaccurate, and loan amount increased without clear explanation.",
          "Consumer-review sites are not regulatory findings, but they can help identify questions regulators may ask when multiple borrowers describe similar transparency or timing problems.",
        ],
        sources: pickSources(["trustpilotMutualMortgageReviews"]),
      },
      {
        heading: "Independent mortgage-review context",
        paragraphs: [
          "Bankrate's review notes that Mutual of Omaha Mortgage does not display current rates and that consumers must submit contact information to get a rate estimate.",
          "The Truth About Mortgage published a review whose headline warns readers to check closing costs, which is relevant background where the Dupont complaint and other borrower reports focus on the gap between advertised or expected terms and final closing economics.",
          "These review sources do not prove misconduct, but they support including rate, fee, and closing-cost transparency in the document-request checklist.",
        ],
        sources: pickSources(["bankrateMutualMortgageReview", "truthAboutMortgageMutualMortgageReview"]),
      },
      {
        heading: "How regulators should use these sources",
        list: [
          "Compare initial loan estimates, revised loan estimates, final closing disclosures, and rate-lock records.",
          "Review borrower communications showing whether a changed product, higher rate, or added fee was explained before closing pressure intensified.",
          "Request branch, broker, and loan-officer complaint history for similar closing-stage allegations.",
          "Separate verified regulatory findings from unresolved borrower allegations, but use repeated allegations to define the scope of document requests.",
        ],
      },
    ],
    sources: sourceCollections.publicMortgageComplaints,
    resources: commonResources,
  },
  {
    slug: "/mutual-of-omaha-mortgage-court-records",
    title: "Mutual of Omaha Mortgage Court and Public Records",
    linkTitle: "MOM Court Records",
    metaTitle: "Mutual of Omaha Mortgage Court Records | CFPB Mortgage Complaint",
    description:
      "Public/free court and public records involving Mutual of Omaha Mortgage, including Longbridge advertising and disclosure litigation, settlement reporting, and related screened records.",
    subheading:
      "A source-backed index of public records used to test transparency, advertising, disclosure, and borrower-facing risk themes.",
    notice: pageNotice,
    companyContext: mutualMortgageContext,
    facts: [
      { label: "Scope", value: "Mutual of Omaha Mortgage and directly related public/free records" },
      { label: "Primary court record", value: "Longbridge Financial v. Mutual of Omaha Mortgage, S.D. Cal. 3:24-cv-01730" },
      { label: "Privacy rule", value: "Case identifiers only; no sensitive personal identifiers" },
    ],
    sections: [
      {
        heading: "How records are classified",
        paragraphs: [
          "This page separates court orders, allegations, settlements, consumer complaints, and screened-but-limited records.",
          "A court order or regulator record is treated differently from a complaint allegation. A settlement or dismissal is not described as an admission unless the source says so.",
        ],
      },
      {
        heading: "Longbridge reverse mortgage advertising case",
        paragraphs: [
          "Longbridge Financial sued Mutual of Omaha Mortgage and related defendants in the Southern District of California, alleging deceptive reverse mortgage advertising and insufficient disclosure around review or comparison websites.",
          "The May 13, 2025 preliminary injunction order granted relief in part and denied relief in part. The order is useful here because it discusses allegedly misleading advertising, disclosures, ownership/control of review sites, and consumer-confusion issues in a mortgage context.",
          "HousingWire later reported that the parties agreed to a confidential settlement and then moved to dismiss the reverse mortgage marketing lawsuit with prejudice. This site treats that reporting as status context, not an admission of liability.",
        ],
        sources: pickSources([
          "longbridgeJustiaOrder2025",
          "longbridgeComplaintPdf2024",
          "longbridgeCchOrderPdf2025",
          "housingWireLongbridgeSettlement2026",
          "housingWireLongbridgeDismissal2026",
        ]),
      },
      {
        heading: "Relevance to the Dupont complaint",
        paragraphs: [
          "The Longbridge case does not prove the Dupont DSCR loan allegations. It is included because it involves Mutual of Omaha Mortgage and public allegations or findings about mortgage-related transparency, advertising, disclosure placement, and consumer-facing presentation.",
          "Those themes overlap with the Dupont complaint's request for review of loan-option presentation, closing-stage pressure, and whether the buyer was given clear and meaningful information before committing to a product path.",
        ],
        links: [
          { label: "Rob Shell DSCR loan steering allegations", to: "/rob-shell-dscr-loan-steering-allegations" },
          { label: "Vicki Smith Legacy South Soren timeline", to: "/vicki-smith-legacy-south-soren-timeline" },
          { label: "Other public Mutual Mortgage complaints", to: "/mutual-mortgage-public-complaints" },
        ],
      },
      {
        heading: "Baesel TCPA record",
        paragraphs: [
          "Baesel v. Mutual of Omaha Mortgage is included as a screened public court record involving Mutual of Omaha Mortgage and alleged consumer-contact conduct.",
          "It is not a close match to the Dupont closing-pressure facts because the available order concerns venue in a TCPA-style consumer-contact dispute, not home-purchase closing or DSCR loan steering.",
        ],
        sources: pickSources(["baeselJustiaOrder2020"]),
      },
      {
        heading: "Records excluded or treated as limited",
        list: [
          "Cases where Mutual of Omaha Mortgage appears only as mortgagee, assignee, lienholder, or nominal party are excluded unless the record alleges borrower-facing misconduct.",
          "Insurance-company records are kept on separate source pages unless the source directly connects the conduct to Mutual of Omaha Mortgage.",
          "Consumer reviews and BBB complaints remain useful pattern sources, but they are not treated as court findings.",
        ],
      },
    ],
    sources: sourceCollections.mutualMortgageCourtRecords,
    resources: commonResources,
  },
  {
    slug: "/legacy-south-soren-development",
    title: "Legacy South and Soren Development Context",
    linkTitle: "Legacy South / Soren",
    metaTitle: "Legacy South and Soren Development Context | CFPB Mortgage Complaint",
    description:
      "Public listing context for the Soren development at 700 N Dupont Ave and its connection to the complaint record.",
    subheading:
      "Development-level context for the properties named in the CFPB complaint.",
    notice: pageNotice,
    facts: [
      { label: "Development", value: "Soren" },
      { label: "Address area", value: "700 N Dupont Ave, Madison, Tennessee" },
      { label: "Public listing description", value: "Legacy South gated community with 120 contemporary townhomes" },
    ],
    sections: [
      {
        heading: "Public listing context",
        paragraphs: [
          "Homes.com and related listing records describe Soren as a Legacy South community at 700 N Dupont Ave with 120 contemporary townhomes.",
          "The complaint concerns two units in this development and alleges that closing pressure affected the borrower's loan-choice window.",
        ],
      },
      {
        heading: "Why the development context matters",
        paragraphs: [
          "A new-home development can involve builder incentives, preferred lenders, title coordination, and tight closing timelines.",
          "Those ordinary structural facts do not prove misconduct, but they identify the records needed to evaluate whether the borrower had meaningful product choice.",
        ],
      },
      {
        heading: "Records to compare",
        list: [
          "Purchase contracts and earnest-money provisions for Units 512 and 514.",
          "Any approved lender or preferred title materials given to buyers.",
          "Sales counselor communications about closing timing.",
          "Loan communications that mention builder deadlines or deposit risk.",
        ],
      },
    ],
    sources: pickSources(["homesSorenUnit303"]),
    resources: commonResources,
  },
  {
    slug: "/vicki-smith-timeline",
    title: "Vicki Smith Public Profile and Timeline",
    linkTitle: "Vicki Smith Profile",
    metaTitle: "Vicki Smith Public Profile and Timeline | CFPB Mortgage Complaint",
    description:
      "Public-record profile and timeline for Vicki Smith, PulteGroup New Home Sales Consultant in the Nashville Metropolitan Area, Legacy South, and Soren complaint references.",
    subheading:
      "A cautious timeline that separates public listing facts from unresolved employment questions.",
    notice: pageNotice,
    personContext: {
      recordId: vickiSmith.id,
      license: vickiSmith.license,
      professionalContext: vickiSmith.professionalContext,
    },
    companyContext: pulteGroupContext,
    facts: [
      { label: "Name used publicly", value: "Vicki Smith" },
      { label: "Public title", value: "New Home Sales Consultant" },
      { label: "Employer listed in public profile", value: "PulteGroup" },
      { label: "Public area listed", value: "Nashville Metropolitan Area" },
      { label: vickiSmith.license.label, value: vickiSmith.license.displayValue },
      { label: "Complaint role", value: "Identified as a Legacy South sales counselor / affiliate broker in complaint materials" },
      { label: "LinkedIn profile", value: "Public profile listed as Vicki Smith at linkedin.com/in/vicki-smith-davidweekleyhomes/" },
      { label: "Public listing reference", value: "Homes.com / Realtracs identifies Vicki Smith with Pulte Homes Tennessee on a 2025 Soren listing" },
      { label: "Unresolved question", value: "The site has no personnel record explaining why or when any affiliation changed" },
    ],
    sections: [
      {
        heading: "What the complaint says",
        paragraphs: [
          "The complaint identifies Vicki Smith in the Legacy South sales context and alleges she communicated closing pressure tied to earnest-money risk.",
          "Those statements are complaint allegations and should remain attributed as such unless supported by independent records.",
        ],
      },
      {
        heading: "Public profile facts",
        paragraphs: [
          "The public LinkedIn profile supplied for review uses the name Vicki Smith, lists PulteGroup, identifies the role as New Home Sales Consultant, and places the profile in the Nashville Metropolitan Area.",
          "The LinkedIn URL used for this dossier is linkedin.com/in/vicki-smith-davidweekleyhomes/.",
        ],
      },
      {
        heading: "What public listing records show",
        paragraphs: [
          "Homes.com / Realtracs identifies Vicki Smith with Pulte Homes Tennessee on a public listing record for 700 N Dupont Ave Unit 303, listed June 14, 2025 and later sold August 26, 2025.",
          "The same record names Laura Sistrunk with Legacy South Brokerage as co-listing agent. Homes.com includes accuracy caveats for listing data, so this site treats the record as useful but independently verifiable context.",
        ],
      },
      {
        heading: "What remains unresolved",
        paragraphs: [
          "The record reviewed for this site does not establish why or how Mrs. Smith's public affiliation changed.",
          "The appropriate framing is that her apparent Legacy South to Pulte Homes Tennessee timeline is relevant to ask about builder-sales practices, not to assert wrongdoing.",
        ],
      },
      {
        heading: "Related Vicki Smith pages",
        paragraphs: [
          "These index pages separate public profile facts, Nashville new-home-sales context, Legacy South/Soren timeline issues, and LinkedIn source handling.",
        ],
        links: [
          { label: "Vicki Smith PulteGroup New Home Sales Consultant", to: "/vicki-smith-pultegroup-new-home-sales-consultant" },
          { label: "Vicki Smith Nashville new home sales", to: "/vicki-smith-nashville-new-home-sales" },
          { label: "Vicki Smith Legacy South Soren timeline", to: "/vicki-smith-legacy-south-soren-timeline" },
          { label: "Vicki Smith LinkedIn public record", to: "/vicki-smith-linkedin-public-record" },
        ],
      },
    ],
    sources: pickSources(["vickiSmithLinkedIn", "homesSorenUnit303", "pulteGroupLinkedIn"]),
    resources: commonResources,
  },
  {
    slug: "/legacy-south-sales-pressure",
    title: "Legacy South Sales Pressure Allegations",
    linkTitle: "Legacy South Pressure",
    metaTitle: "Legacy South Sales Pressure Allegations | CFPB Mortgage Complaint",
    description:
      "Complaint allegations about Legacy South sales pressure, earnest money, and closing timing.",
    subheading:
      "A focused page on the complaint's sales-pressure narrative and the records needed to test it.",
    notice: pageNotice,
    sections: [
      {
        heading: "Allegation stated in the complaint",
        paragraphs: [
          "The complaint alleges that Legacy South sales-side communications placed pressure on the borrower to close and warned that earnest-money deposits were at risk.",
          "The rebuttal argues that this pressure affected whether the borrower could realistically reject the DSCR product and seek another financing path.",
        ],
      },
      {
        heading: "Why timing matters",
        paragraphs: [
          "Closing pressure can affect consent even when a borrower sends a message indicating readiness to proceed.",
          "The dispute is whether that readiness reflected informed choice or a constrained decision made under deposit and deadline risk.",
        ],
      },
      {
        heading: "Records needed",
        list: [
          "All sales-side communications about earnest money and closing deadlines.",
          "Purchase contract provisions governing deposit forfeiture.",
          "Any builder or title messages copied to lender-side personnel.",
          "A timeline comparing financing disclosures with contract deadlines.",
        ],
      },
    ],
    sources: pickSources(["homesSorenUnit303"]),
    resources: commonResources,
  },
  {
    slug: "/pultegroup-transition-context",
    title: "PulteGroup Transition Context",
    linkTitle: "PulteGroup Transition",
    metaTitle: "PulteGroup Transition Context | CFPB Mortgage Complaint",
    description:
      "Context for the public references connecting Mrs. Smith to Pulte Homes Tennessee and the limits of that evidence.",
    subheading:
      "How to discuss the post-Legacy South Pulte reference without overstating it.",
    notice: pageNotice,
    personContext: {
      recordId: vickiSmith.id,
      license: vickiSmith.license,
      professionalContext: vickiSmith.professionalContext,
    },
    companyContext: pulteGroupContext,
    facts: [
      { label: "PulteGroup public profile", value: "Construction company headquartered in Atlanta, Georgia" },
      { label: "Listing record", value: "Homes.com / Realtracs identifies Vicki Smith with Pulte Homes Tennessee on a 2025 Soren sale" },
    ],
    sections: [
      {
        heading: "Public affiliation reference",
        paragraphs: [
          "The public listing record identifies Vicki Smith with Pulte Homes Tennessee in 2025.",
          "PulteGroup's LinkedIn page identifies the company as a construction business and public company. This site does not assume the exact legal relationship between Pulte Homes Tennessee and PulteGroup beyond what public sources establish.",
        ],
      },
      {
        heading: "Use in the complaint narrative",
        paragraphs: [
          "The transition is relevant because the complaint questions whether builder sales practices at Soren placed pressure on financing decisions.",
          "It does not prove that similar conduct occurred at Pulte, and it does not prove why Mrs. Smith changed affiliation.",
        ],
      },
      {
        heading: "How the timeline should be read",
        paragraphs: [
          "The site should say that public records identify a later Pulte Homes Tennessee affiliation after the Legacy South sales context referenced in the complaint.",
          "The site should describe the concern as a reason to request records and review practices, not as a conclusion about misconduct.",
        ],
      },
    ],
    sources: pickSources(["homesSorenUnit303", "pulteGroupLinkedIn"]),
    resources: commonResources,
  },
  {
    slug: "/pulte-del-webb-defect-lawsuit",
    title: "Pulte / Del Webb Defect Lawsuit Reporting",
    linkTitle: "Pulte / Del Webb Lawsuit",
    metaTitle: "Pulte / Del Webb Defect Lawsuit Reporting | CFPB Mortgage Complaint",
    description:
      "Summary of FOX5 reporting on the Del Webb Lake Las Vegas construction defect dispute involving Pulte Group.",
    subheading:
      "Separate third-party reporting used as builder-risk context, not evidence of the Dupont mortgage allegations.",
    notice: pageNotice,
    facts: [
      { label: "News outlet", value: "FOX5 Las Vegas" },
      { label: "Published", value: "May 15, 2026; updated May 18, 2026" },
      { label: "Reported status", value: "Judge ordered the dispute to arbitration" },
      { label: "Reported allegation", value: "Homes cracking and sinking in Del Webb Lake Las Vegas" },
    ],
    sections: [
      {
        heading: "What FOX5 reports",
        paragraphs: [
          "FOX5 reports that a Clark County District Court judge ordered a construction defect dispute involving the Del Webb Lake Las Vegas 55+ community and Pulte Group to arbitration.",
          "The report states that the homeowners and HOA allege faulty construction and soil compaction problems caused cracking, settlement, and related damage.",
        ],
      },
      {
        heading: "Pulte's reported response",
        paragraphs: [
          "FOX5 reports that Pulte Group agreed with the court's arbitration order and said arbitration was the appropriate forum for resolving the claims.",
          "The article also includes an editor's note stating that Pulte clarified the legal action was brought by the HOA as a legal entity, not individual homeowners.",
        ],
      },
      {
        heading: "How this site uses it",
        paragraphs: [
          "This reporting is included only as current public context involving a builder referenced in the expanded record.",
          "It does not prove the Dupont loan allegations, and it does not establish that Mrs. Smith or Pulte committed fraud, bribery, or kickbacks.",
        ],
      },
    ],
    sources: pickSources(["fox5PulteDelWebb2026", "pulteGroupLinkedIn"]),
    resources: commonResources,
  },
  {
    slug: "/builder-lender-sales-channel-risk",
    title: "Builder-Lender Sales Channel Risk",
    linkTitle: "Builder-Lender Risk",
    metaTitle: "Builder-Lender Sales Channel Risk | CFPB Mortgage Complaint",
    description:
      "Explanation of why builder-lender relationships require careful documentation in a mortgage steering review.",
    subheading:
      "The risk framework behind the complaint's builder, lender, title, and closing-timeline questions.",
    notice: pageNotice,
    sections: [
      {
        heading: "Why the channel matters",
        paragraphs: [
          "A builder-lender channel can make buying easier when incentives and communications are transparent.",
          "The same channel can create risk when a buyer is told that one lender path or loan product is the only practical way to preserve a purchase contract.",
        ],
      },
      {
        heading: "Complaint-specific concern",
        paragraphs: [
          "The complaint alleges that sales pressure and lender product presentation operated together to push closing regardless of the borrower's later cost concerns.",
          "That claim is unresolved and should be tested through records, not assumed from the mere existence of a builder-lender relationship.",
        ],
      },
      {
        heading: "Records that matter most",
        list: [
          "Preferred lender disclosures and any incentive terms.",
          "Builder and lender communications about the specific borrower.",
          "Title and closing coordination records.",
          "Loan comparison materials and borrower acknowledgment logs.",
        ],
      },
    ],
    sources: pickSources(["homesSorenUnit303", "regulationZ"]),
    resources: commonResources,
  },
  {
    slug: "/possible-builder-lender-steering",
    title: "Possible Builder-Lender Steering Question",
    linkTitle: "Builder-Lender Steering",
    metaTitle: "Possible Builder-Lender Steering Question | CFPB Mortgage Complaint",
    description:
      "The complaint's steering question involving builder timing pressure and Mutual of Omaha Mortgage product presentation.",
    subheading:
      "A sourced question for regulators: was the borrower given meaningful product choice?",
    notice: pageNotice,
    sections: [
      {
        heading: "Question presented",
        paragraphs: [
          "The complaint asks whether the borrower was steered into a higher-cost DSCR interest-only loan because alternatives were not meaningfully presented.",
          "The builder-lender question is whether sales pressure around closing and loan presentation worked together to narrow practical choice.",
          "The complainant specifically alleges that Vicki Smith's builder-side closing pressure and Rob Shell's loan-option presentation worked together to push the buyer toward closing, and asks regulators to review whether that conduct was unlawful steering or manipulation.",
        ],
      },
      {
        heading: "What would support the concern",
        list: [
          "Evidence that lower-cost options were available but not disclosed.",
          "Evidence that the borrower requested alternatives and received no written comparison.",
          "Evidence that closing pressure was communicated while only one loan path was presented.",
          "Evidence that compensation or referral structures favored the selected path.",
        ],
      },
      {
        heading: "What would cut against the concern",
        list: [
          "Clear written comparison of eligible products before commitment.",
          "Documented conventional eligibility analysis and decline rationale.",
          "Disclosure logs showing timely receipt and practical review time.",
          "Compensation records showing no channel bias or improper referral benefit.",
        ],
      },
    ],
    sources: pickSources(["regulationZ", "homesSorenUnit303", "vickiSmithLinkedIn", "robShellOfficial"]),
    resources: commonResources,
  },
  {
    slug: "/possible-bribery-kickback-question",
    title: "Possible Bribery or Kickback Question",
    linkTitle: "Bribery / Kickback Question",
    metaTitle: "Possible Bribery or Kickback Question | CFPB Mortgage Complaint",
    description:
      "Careful framing of the complaint's bribery and kickback concerns involving Mutual Mortgage and Legacy South.",
    subheading:
      "An investigative question that must be tested through compensation, referral, and closing records.",
    notice: pageNotice,
    facts: [
      { label: "Complaint theory", value: "Builder and lender channels may have aligned to push closings" },
      { label: "Public background", value: "Good Jobs First lists a separate 2008 Mutual of Omaha kickbacks and bribery record" },
      { label: "Current status", value: "No cited source on this site proves a Dupont kickback" },
    ],
    sections: [
      {
        heading: "How the allegation is framed",
        paragraphs: [
          "The complaint and rebuttal raise concerns that Mutual of Omaha Mortgage and Legacy South may have had incentives to close sales regardless of whether the loan structure was best for the borrower.",
          "This page presents that as an investigative question. It does not state that bribery or kickbacks occurred in the Dupont transactions.",
        ],
      },
      {
        heading: "Why the Connecticut record is relevant but limited",
        paragraphs: [
          "Good Jobs First lists a separate 2008 Connecticut record for Mutual of Omaha Insurance Co. under 'kickbacks and bribery.'",
          "That record is relevant to compensation-transparency context, but it does not prove a mortgage referral or builder kickback in Tennessee.",
        ],
      },
      {
        heading: "Records needed to test the question",
        list: [
          "Any referral, marketing, preferred lender, or co-op agreement between builder-side and lender-side entities.",
          "Loan-level compensation records for the selected product.",
          "Builder incentive documents and title-company coordination records.",
          "Internal communications about preserving sales, closing deadlines, or deposit forfeiture.",
        ],
      },
    ],
    sources: pickSources(["goodJobsFirstConnecticut2008", "goodJobsFirstParent", "homesSorenUnit303"]),
    resources: commonResources,
  },
  {
    slug: "/earnest-money-pressure",
    title: "Earnest Money Pressure",
    linkTitle: "Earnest Money Pressure",
    metaTitle: "Earnest Money Pressure | CFPB Mortgage Complaint",
    description:
      "How earnest-money risk is alleged to have affected the borrower's ability to compare mortgage options before closing.",
    subheading:
      "The complaint's pressure theory and the contract records needed to test it.",
    notice: pageNotice,
    facts: [
      { label: "Deposit amount alleged", value: "$10,000 per property" },
      { label: "Dispute", value: "Whether the borrower had practical room to reject the DSCR option" },
    ],
    sections: [
      {
        heading: "Complaint allegation",
        paragraphs: [
          "The complaint alleges that the borrower faced pressure to close because failure to proceed could put earnest-money deposits at risk.",
          "The rebuttal states that agreement-to-proceed language should be read in that context, not as proof of unrestricted product preference.",
        ],
      },
      {
        heading: "Why it matters",
        paragraphs: [
          "A borrower can technically sign or proceed while still lacking meaningful choice if the practical alternative is losing a deposit or missing a hard deadline.",
          "The question is whether disclosure timing and product-option presentation gave the borrower a realistic chance to compare alternatives before that pressure point.",
        ],
      },
      {
        heading: "Records needed",
        list: [
          "Purchase contract deposit provisions.",
          "Written warnings or communications about forfeiture risk.",
          "Timeline of disclosure delivery against closing deadlines.",
          "Any requests to extend closing or seek alternate financing.",
        ],
      },
    ],
    sources: pickSources(["homesSorenUnit303"]),
    resources: commonResources,
  },
  {
    slug: "/dscr-loan-explainer",
    title: "DSCR Loan Explainer",
    linkTitle: "DSCR Explainer",
    metaTitle: "DSCR Loan Explainer | CFPB Mortgage Complaint",
    description:
      "Plain-English explanation of DSCR investment-property loans and why DSCR selection is disputed in the complaint.",
    subheading:
      "A neutral explanation of the loan type at the center of the dispute.",
    notice: pageNotice,
    sections: [
      {
        heading: "What DSCR generally means",
        paragraphs: [
          "DSCR stands for debt service coverage ratio. In investment-property lending, a DSCR program generally looks to property cash flow rather than only the borrower's personal income.",
          "That structure can be legitimate for investors, but it can carry different pricing, documentation, and risk characteristics than conventional financing.",
        ],
      },
      {
        heading: "Why it is disputed here",
        paragraphs: [
          "Mutual states the DSCR product was suitable because the borrower did not meet conventional personal-income qualification expectations at the time.",
          "The rebuttal argues that the DSCR path was presented as the only practical option and that written alternatives were not provided during the decision window.",
        ],
      },
      {
        heading: "Review question",
        paragraphs: [
          "The core question is not whether DSCR loans are always improper. The question is whether this borrower was given a fair, documented comparison of available options before committing to the selected product.",
        ],
      },
    ],
    sources: pickSources(["regulationZ"]),
    resources: commonResources,
  },
  {
    slug: "/non-qm-interest-only-risk",
    title: "Non-QM Interest-Only Risk",
    linkTitle: "Interest-Only Risk",
    metaTitle: "Non-QM Interest-Only Risk | CFPB Mortgage Complaint",
    description:
      "Why non-QM interest-only terms are central to the alleged financial harm and option-transparency dispute.",
    subheading:
      "Risk context for the product structure named in the complaint.",
    notice: pageNotice,
    sections: [
      {
        heading: "Product structure concern",
        paragraphs: [
          "The complaint describes the selected loans as non-QM, DSCR, interest-only products with higher long-term cost than alternatives the complainant believes should have been explored.",
          "Interest-only periods can reduce initial principal payments but may increase long-term cost or refinancing dependence depending on the rate, term, and investor strategy.",
        ],
      },
      {
        heading: "Disputed suitability",
        paragraphs: [
          "Mutual states the product fit the borrower's investment-property profile. The rebuttal argues the product was not meaningfully compared against other available paths.",
          "A suitability review depends on contemporaneous records, not later general claims about what products existed.",
        ],
      },
      {
        heading: "Records needed",
        list: [
          "Payment and amortization comparisons for DSCR versus conventional alternatives.",
          "Rate sheets and lock confirmations.",
          "Borrower communications explaining interest-only risks.",
          "Documentation of why the selected structure was recommended.",
        ],
      },
    ],
    sources: pickSources(["regulationZ"]),
    resources: commonResources,
  },
  {
    slug: "/rate-sheet-option-transparency",
    title: "Rate Sheet and Option Transparency",
    linkTitle: "Rate Sheet Transparency",
    metaTitle: "Rate Sheet and Option Transparency | CFPB Mortgage Complaint",
    description:
      "Why lock-date rate sheets and product menus are central to the complaint's option-transparency request.",
    subheading:
      "The record request most likely to clarify what options were available when decisions were made.",
    notice: pageNotice,
    sections: [
      {
        heading: "Why rate sheets matter",
        paragraphs: [
          "Rate sheets and product matrices can show what programs and pricing were available at a specific time.",
          "They are more useful than after-the-fact statements because they tie product availability to the actual application and lock windows.",
        ],
      },
      {
        heading: "Current dispute",
        paragraphs: [
          "The rebuttal states that lower-cost and conventional options were requested but not presented in writing during the active decision window.",
          "Mutual disputes the use of later 2024-2025 rate information as proof of 2023 availability and states the selected product reflected the borrower's profile at the time.",
        ],
      },
      {
        heading: "Minimum production request",
        list: [
          "All rate sheets available on the relevant lock dates.",
          "Product matrices and overlays applicable to the borrower and properties.",
          "Any lock desk notes or exception requests.",
          "Side-by-side comparisons actually delivered to the borrower.",
        ],
      },
    ],
    sources: pickSources(["regulationZ"]),
    resources: commonResources,
  },
  {
    slug: "/business-purpose-exemption-limits",
    title: "Business-Purpose Exemption Limits",
    linkTitle: "Business-Purpose Limits",
    metaTitle: "Business-Purpose Exemption Limits | CFPB Mortgage Complaint",
    description:
      "How the business-purpose exemption is disputed in the complaint and rebuttal.",
    subheading:
      "The distinction between disclosure exemptions and broader anti-steering or UDAAP review.",
    notice: pageNotice,
    sections: [
      {
        heading: "Mutual's position",
        paragraphs: [
          "Mutual relies on business-purpose classification and signed affidavits to support its position that certain consumer-disclosure rules do not apply.",
          "Mutual states the loans were originated and closed in compliance with applicable law.",
        ],
      },
      {
        heading: "Rebuttal position",
        paragraphs: [
          "The rebuttal agrees the loans were investment-purpose but argues that business-purpose classification does not end anti-steering or UDAAP review.",
          "The rebuttal also argues that signed affidavits do not answer whether the borrower was misled about product availability or deprived of meaningful comparison.",
        ],
      },
      {
        heading: "Review focus",
        paragraphs: [
          "This site should avoid deciding the legal issue. The page should identify the competing arguments and point readers to the records needed for legal review.",
        ],
      },
    ],
    sources: pickSources(["regulationZ"]),
    resources: commonResources,
  },
  {
    slug: "/compensation-conflict-review",
    title: "Compensation Conflict Review",
    linkTitle: "Compensation Review",
    metaTitle: "Compensation Conflict Review | CFPB Mortgage Complaint",
    description:
      "Requested review of compensation, referral, and incentive records tied to the disputed loan path.",
    subheading:
      "A focused record request for testing steering, referral, and channel-bias concerns.",
    notice: pageNotice,
    sections: [
      {
        heading: "Why compensation records matter",
        paragraphs: [
          "Compensation records can show whether a loan officer, branch, broker channel, builder channel, or other participant had an incentive to prefer one product path.",
          "They can also cut against the allegation if the records show no improper or higher compensation tied to the selected product.",
        ],
      },
      {
        heading: "Current dispute",
        paragraphs: [
          "Mutual states there was no incentive to steer because brokered DSCR compensation was lower than conventional in-house compensation.",
          "The rebuttal argues that steering can also occur through misrepresentation, incomplete option presentation, or failure to provide lower-cost choices, even if the selected product was not the highest-compensating path.",
          "The rebuttal further alleges that Mutual of Omaha Mortgage's complaint response protects broker and builder-sales participants, including Rob Shell and Vicki Smith, while the loans moved through NewFi Lending and later to Planet Home Lending.",
        ],
      },
      {
        heading: "Records requested",
        list: [
          "Loan-level compensation records for both subject loans.",
          "Compensation schedules for DSCR, brokered, conventional, and in-house channels.",
          "Any builder, referral, marketing, or preferred partner compensation arrangements.",
          "Internal compliance review notes on compensation and fees.",
        ],
      },
    ],
    sources: pickSources(["regulationZ", "goodJobsFirstConnecticut2008"]),
    resources: commonResources,
  },
  {
    slug: "/records-needed-from-mutual",
    title: "Records Needed From Mutual",
    linkTitle: "Records From Mutual",
    metaTitle: "Records Needed From Mutual | CFPB Mortgage Complaint",
    description:
      "Specific records requested from Mutual of Omaha Mortgage to evaluate option presentation, disclosures, suitability, and compensation.",
    subheading:
      "A practical production checklist for the originating mortgage channel.",
    notice: pageNotice,
    companyContext: mutualMortgageContext,
    sections: [
      {
        heading: "Origination records",
        list: [
          "Full application files for Units 512 and 514.",
          "Loan officer notes and correspondence.",
          "Product eligibility analysis and conventional decline rationale.",
          "Rate sheets, lock confirmations, and pricing exception records.",
        ],
      },
      {
        heading: "Disclosure records",
        list: [
          "Disclosure packages and delivery audit logs.",
          "Borrower acknowledgment timestamps.",
          "Sample mortgage comparison materials referenced by Mutual.",
          "Any final closing disclosures and business-purpose affidavits.",
        ],
      },
      {
        heading: "Compensation records",
        list: [
          "Loan-level compensation records.",
          "Brokered DSCR and in-house conventional compensation schedules.",
          "Compliance review notes on fees, points, and discount charges.",
          "Any communication involving builder or referral incentives.",
        ],
      },
    ],
    sources: pickSources(["mutualMortgageHome", "robShellOfficial", "regulationZ"]),
    resources: commonResources,
  },
  {
    slug: "/records-needed-from-builder",
    title: "Records Needed From Builder-Side Participants",
    linkTitle: "Records From Builder",
    metaTitle: "Records Needed From Builder-Side Participants | CFPB Mortgage Complaint",
    description:
      "Specific builder-side and sales-side records requested to evaluate closing pressure and lender coordination allegations.",
    subheading:
      "A practical production checklist for Legacy South, sales, title, and closing context.",
    notice: pageNotice,
    sections: [
      {
        heading: "Sales and contract records",
        list: [
          "Purchase contracts for the subject units.",
          "Earnest-money provisions and any amendment history.",
          "Sales counselor communications about deadlines, deposit risk, and financing.",
          "Buyer-facing incentive or approved-lender documents.",
        ],
      },
      {
        heading: "Coordination records",
        list: [
          "Communications among builder, lender, title, and closing participants.",
          "Records showing whether sales personnel received updates on loan status.",
          "Any approved title or lender arrangements.",
          "Any internal notes about preserving sales or avoiding fallout.",
        ],
      },
      {
        heading: "Why these records matter",
        paragraphs: [
          "These documents test the complaint's allegation that sales pressure and lender presentation constrained practical choice.",
          "They may also show that the builder-side channel did not influence loan selection, which is why the records are important for both sides.",
        ],
      },
    ],
    sources: pickSources(["homesSorenUnit303"]),
    resources: commonResources,
  },
  {
    slug: "/records-needed-from-newfi-planet",
    title: "Records Needed From NewFi and Planet",
    linkTitle: "Records From NewFi / Planet",
    metaTitle: "Records Needed From NewFi and Planet | CFPB Mortgage Complaint",
    description:
      "Records requested from NewFi Lending and Planet Home Lending to clarify funding, transfer, and servicing issues.",
    subheading:
      "A focused checklist for the funding channel and later transfer/servicing questions.",
    notice: pageNotice,
    sections: [
      {
        heading: "NewFi Lending records",
        list: [
          "Funding and underwriting file records for both loans.",
          "Program matrix and pricing available through the wholesale or broker channel.",
          "Communications with Mutual of Omaha Mortgage about eligibility and disclosures.",
          "Any transfer, sale, or servicing handoff notices.",
        ],
      },
      {
        heading: "Planet Home Lending records",
        list: [
          "Transfer notices and servicing onboarding records.",
          "Dates and contents of borrower notices.",
          "Records showing when Planet received or assumed servicing responsibilities.",
          "Any borrower communications about the transfer.",
        ],
      },
      {
        heading: "Why these records matter",
        paragraphs: [
          "Mutual states it acted as broker and directs post-funding transfer issues to NewFi Lending.",
          "The rebuttal argues that transfer-notice clarity remains unresolved. The records above would help separate origination issues from transfer and servicing issues.",
        ],
      },
    ],
    sources: pickSources(["regulationZ"]),
    resources: commonResources,
  },
  {
    slug: "/complaint-to-regulators-map",
    title: "Complaint to Regulators Map",
    linkTitle: "Regulator Map",
    metaTitle: "Complaint to Regulators Map | CFPB Mortgage Complaint",
    description:
      "Map of regulatory issues raised by the complaint and the agencies or records relevant to each issue.",
    subheading:
      "A reader-friendly map from allegation type to the records that could resolve it.",
    notice: pageNotice,
    sections: [
      {
        heading: "CFPB review",
        list: [
          "UDAAP concerns tied to product presentation and practical choice.",
          "Regulation Z anti-steering concerns tied to available lower-cost options.",
          "Disclosure timing and transfer-notice questions where applicable.",
        ],
      },
      {
        heading: "State and other review channels",
        list: [
          "State mortgage regulators for lender and originator licensing questions.",
          "State attorney general review where unfair or deceptive practices are alleged.",
          "HUD or RESPA review where referral compensation or settlement-service kickbacks are alleged.",
        ],
      },
      {
        heading: "Record-first approach",
        paragraphs: [
          "The site should direct readers to the documents that could confirm or refute each issue rather than presenting unresolved claims as findings.",
        ],
      },
    ],
    sources: pickSources(["cfpbComplaintPortal", "regulationZ", "nmlsConsumerAccess"]),
    resources: commonResources,
  },
  {
    slug: "/media-public-record-summary",
    title: "Media and Public Record Summary",
    linkTitle: "Media / Public Records",
    metaTitle: "Media and Public Record Summary | CFPB Mortgage Complaint",
    description:
      "Summary page collecting public records and third-party reporting used in the expanded Mutual Mortgage section.",
    subheading:
      "A clean reader path through source records, profile pages, and third-party reporting.",
    notice: pageNotice,
    sections: [
      {
        heading: "Regulatory records",
        paragraphs: [
          "Good Jobs First lists Mutual of Omaha parent-company records across consumer-protection-related and competition-related categories.",
          "The pages in this section separate mortgage-company records from insurance-company records and explain the limits of each source.",
        ],
      },
      {
        heading: "Court and litigation records",
        paragraphs: [
          "The Mutual of Omaha Mortgage court-records page collects public/free court sources and reporting, including the Longbridge advertising and disclosure litigation.",
          "Those records are used for transparency and pattern questions, not as proof of the Dupont loan allegations unless a court or regulator source directly establishes a fact.",
        ],
      },
      {
        heading: "People and entity records",
        paragraphs: [
          "Rob Shell's public Mutual of Omaha Mortgage page establishes his public role and NMLS number.",
          "Homes.com / Realtracs listing records provide public context for Soren, Vicki Smith, Legacy South, and Pulte Homes Tennessee references, with the usual listing-data accuracy caveats.",
        ],
      },
      {
        heading: "News reporting",
        paragraphs: [
          "FOX5 reporting on Pulte and Del Webb is included as current public builder-risk context, not as evidence that the Dupont mortgage allegations are true.",
        ],
      },
      {
        heading: "Public borrower complaints and reviews",
        paragraphs: [
          "BBB complaints, Trustpilot reviews, and mortgage-review coverage are included to identify recurring public themes around closing costs, rate changes, product clarity, and document timing.",
          "Those sources are not treated as findings of fraud. They are used to frame document requests and pattern questions for regulators.",
        ],
      },
    ],
    sources: [
      ...sourceCollections.mutualRegulatoryRecords,
      ...sourceCollections.mutualMortgageCourtRecords,
      ...sourceCollections.peopleAndEntities,
      ...sourceCollections.publicMortgageComplaints,
      ...pickSources(["fox5PulteDelWebb2026"]),
    ],
    resources: commonResources,
  },
  {
    slug: "/source-index-reading-guide",
    title: "Source Index and Reading Guide",
    linkTitle: "Source Reading Guide",
    metaTitle: "Source Index and Reading Guide | CFPB Mortgage Complaint",
    description:
      "Reading guide for the expanded Mutual Mortgage source pages and supporting topic pages.",
    subheading:
      "How to read the new pages without confusing context, allegation, and proof.",
    notice: pageNotice,
    sections: [
      {
        heading: "Start with the source pages",
        list: [
          "Read the Massachusetts 2025 source page for the mortgage-company consent order.",
          "Read the Mutual of Omaha Mortgage court-records page for public/free litigation and reporting context.",
          "Read the South Carolina and South Dakota source pages as related insurance-company context.",
          "Read the Connecticut 2008 source page for the separate kickbacks and bribery record listed by Good Jobs First.",
        ],
      },
      {
        heading: "Then read the people and builder pages",
        list: [
          "Use the Rob Shell pages to understand the public role and the complaint-specific allegations.",
          "Use the Legacy South, Vicki Smith, and Pulte pages to understand the development and public affiliation timeline.",
          "Use the builder-lender pages to understand what documents would confirm or refute the complaint's theory.",
        ],
      },
      {
        heading: "Attribution rule",
        paragraphs: [
          "Regulatory records establish what the regulator or database record says. Complaint pages establish what the complainant alleges. Company-response pages establish what Mutual states.",
          "The site should not merge those categories into one conclusion unless a cited source does the same.",
        ],
      },
    ],
    sources: pickSources(mutualRecordSources.concat(builderSources)),
    resources: commonResources,
  },
];
